import { getBgImageUrl, portableTextSerializer } from '@/utils/helpers';

import BlockContent from '@sanity/block-content-to-react';
import React from 'react';
import styles from './styles.module.css';

interface SanityImageHotspot {
  x: number;
  y: number;
}

interface SanityImage {
  hotspot?: SanityImageHotspot;
  [key: string]: unknown;
}

interface FoldProps {
  heading: string;
  subHeading?: string;
  subtitle?: Record<string, unknown>;
  featuredSubtitle?: string;
  textColor?: Record<string, unknown>;
  alignment?: string;
  bgImage?: SanityImage | string;
  bgImageMobile?: SanityImage | string;
  bgGradient?: Record<string, unknown>;
  bgColor?: Record<string, unknown>;
  bgSize?: string;
  squareOnMobile?: boolean;
  coloredTail?: boolean; // New prop to indicate if the last word should be colored
}

function getHotspotPosition(img: SanityImage | string | undefined): string | undefined {
  if (!img || typeof img === 'string') return undefined;
  const { hotspot } = img;
  if (!hotspot || typeof hotspot.x !== 'number' || typeof hotspot.y !== 'number') return undefined;
  return `${hotspot.x * 100}% ${hotspot.y * 100}%`;
}

const Fold: React.FC<FoldProps> = ({
  heading,
  alignment = 'left',
  bgImage,
  bgImageMobile,
  subHeading,
  subtitle,
  featuredSubtitle,
  textColor,
  bgColor,
  bgGradient,
  bgSize,
  squareOnMobile,
}) => {
  const wrapperRef = React.useRef(null);
  const [backgroundImage] = React.useState<string>(() => getBgImageUrl(bgImage));
  const [mobileBackgroundImage] = React.useState<string>(() => getBgImageUrl(bgImageMobile));
  const hasMobileImage = mobileBackgroundImage !== 'none';
  const bgPosition = getHotspotPosition(bgImage);
  const bgPositionMobile = getHotspotPosition(bgImageMobile) ?? bgPosition;

  React.useEffect(() => {
    const typedBgColor = bgColor as { hex?: string } | undefined;
    const typedTextColor = textColor as { hex?: string } | undefined;
    const typedBgGradient = bgGradient as
      | {
          gradientDirection?: string;
          colorStart?: { hex?: string };
          colorStop?: { hex?: string };
          opacity?: number;
        }
      | undefined;

    if (typedBgColor?.hex) {
      wrapperRef.current.style.setProperty('--bgColor', `${typedBgColor.hex}`);
    }
    if (typedBgGradient?.gradientDirection && typedBgGradient?.colorStart?.hex && typedBgGradient?.colorStop?.hex) {
      wrapperRef.current.style.setProperty(
        '--bgGradient',
        `linear-gradient(to
          ${typedBgGradient.gradientDirection},
          ${typedBgGradient.colorStart.hex} 0%,
          ${typedBgGradient.colorStop.hex} 100%)`,
      );

      if (typedBgGradient?.opacity) {
        wrapperRef.current.style.setProperty('--gradientOpacity', `${typedBgGradient.opacity}`);
      }
    }
    if (typedTextColor?.hex) {
      wrapperRef.current.style.setProperty('--fontColor', `${typedTextColor.hex}`);
    }
  }, [bgColor, textColor, bgGradient]);

  return (
    <section
      className={styles.fold}
      ref={wrapperRef}
      style={
        {
          '--bgImage': backgroundImage,
          ...(hasMobileImage
            ? { '--bgImageMobile': mobileBackgroundImage }
            : squareOnMobile
              ? { '--bgImageMobile': 'none' }
              : {}),
          ...(bgPosition ? { '--bgPosition': bgPosition } : {}),
          ...(bgPositionMobile ? { '--bgPositionMobile': bgPositionMobile } : {}),
          ...(hasMobileImage ? { '--bgSizeMobile': 'cover' } : bgSize ? { '--bgSizeMobile': bgSize } : {}),
          ...(squareOnMobile && hasMobileImage ? { '--foldMobileHeight': 'min(100vw, 30rem)' } : {}),
        } as React.CSSProperties
      }
    >
      <div className={`sm:w-full ${styles.foldContent}`}>
        <h1
          className={`${styles[alignment]} ${
            styles.foldTitle
          } ${featuredSubtitle && styles.foldTitleWithFeaturedSubtitle}`}
        >
          {heading}
        </h1>
        {featuredSubtitle && (
          <div className={`${styles[alignment]}`}>
            <h2 className={`${styles.featuredSubtitle}`}>{featuredSubtitle}</h2>
          </div>
        )}
        {(subtitle && (
          <div className={`${styles[alignment]} ${styles.foldSubheading}`}>
            <BlockContent blocks={subtitle} serializers={portableTextSerializer} />
          </div>
        )) || <p className={`${styles[alignment]} ${styles.foldSubheading}`}>{subHeading}</p>}
      </div>
    </section>
  );
};

export default Fold;
